import * as React from "react";
import { useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
  STATE_LABEL,
  STATE_COLOR,
  toDateInput,
  toInclusiveInput,
  toStoredThrough,
} from "../utils/eligibilityDisplay";
import { confirmEligibilityPeriod } from "../services/eligibilityApi";
import EligibilityImpactAck from "./EligibilityImpactAck";

// Review & Confirm / Adjust & Confirm for an active proposal. One dialog: Start/End editable,
// everything else read-only. The primary action label reflects whether the dates were edited. Impact
// (design §7.2 #6) is computed server-side on submit (Option B): if accepted LK/SI seasons would fall
// outside the resulting eligibility, the server commits nothing and returns the impact; the shared
// EligibilityImpactAck then shows the affected seasons and a required acknowledgment checkbox before
// a second, acknowledged submit.
export default function EligibilityConfirmDialog({ open, period, comCode, franchiseName, onClose, onConfirmed }) {
  const originalStart = period ? toDateInput(period.enabled_from_date) : "";
  const originalEnd = period ? toInclusiveInput(period.enabled_through_date) : "";

  const [start, setStart] = useState(originalStart);
  const [end, setEnd] = useState(originalEnd);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [impact, setImpact] = useState(null); // { count, seasons } once the server requires ack
  const [acknowledged, setAcknowledged] = useState(false);

  if (!period) return null;

  const adjusted = start !== originalStart || end !== originalEnd;
  const primaryLabel = adjusted ? "Adjust & Confirm" : "Confirm";
  const needsAck = impact !== null && impact.count > 0;

  // Any date edit invalidates a shown impact review — force a fresh server recompute.
  const changeStart = (v) => { setStart(v); setImpact(null); setAcknowledged(false); };
  const changeEnd = (v) => { setEnd(v); setImpact(null); setAcknowledged(false); };

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleConfirm = async () => {
    setError(null);
    if (!start) { setError("Start Date is required."); return; }
    if (end && end < start) { setError("End Date must be on or after Start Date."); return; }

    setSubmitting(true);
    try {
      const result = await confirmEligibilityPeriod(period.enablement_period_id, {
        enabledFromDate: start,
        enabledThroughDate: toStoredThrough(end),
        acknowledged,
      });
      if (result.requiresAcknowledgment) {
        setImpact(result.impact);   // show the warning + checkbox; user must acknowledge
        setSubmitting(false);
        return;
      }
      onConfirmed(
        adjusted ? `Adjusted and confirmed for ${comCode}` : `Eligibility confirmed for ${comCode}`
      );
    } catch (e) {
      setError(e.message || "Confirm failed.");
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Review &amp; confirm eligibility proposal</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Typography variant="body2">
            <strong>{comCode}</strong>{franchiseName ? ` · ${franchiseName}` : ""}
          </Typography>
          <Chip
            size="small"
            label={STATE_LABEL[period.eligibility_state] ?? period.eligibility_state}
            color={STATE_COLOR[period.eligibility_state] ?? "default"}
          />
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Adjust the Start or End date if the eligibility window differs, then confirm. Left unchanged,
          it confirms the proposal as-is.
        </Typography>

        <Box sx={{ display: "flex", gap: 2, mb: 1.5 }}>
          <TextField
            type="date" label="Start Date" required value={start}
            onChange={(e) => changeStart(e.target.value)}
            InputLabelProps={{ shrink: true }} size="small" fullWidth
          />
          <TextField
            type="date" label="End Date (inclusive; empty = open-ended)" value={end}
            onChange={(e) => changeEnd(e.target.value)}
            InputLabelProps={{ shrink: true }} size="small" fullWidth
          />
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 2, rowGap: 0.5, mb: 1.5 }}>
          <Typography variant="body2" color="text.secondary">Evidence / reason</Typography>
          <Typography variant="body2">{period.evidence_note || "—"}</Typography>
          <Typography variant="body2" color="text.secondary">Source</Typography>
          <Typography variant="body2">{period.source || "—"}</Typography>
          <Typography variant="body2" color="text.secondary">Confidence</Typography>
          <Typography variant="body2">{period.confidence || "—"}</Typography>
          <Typography variant="body2" color="text.secondary">Horizon-limited</Typography>
          <Typography variant="body2">{period.horizon_limited ? "Yes" : "No"}</Typography>
        </Box>

        <EligibilityImpactAck impact={impact} acknowledged={acknowledged} onChange={setAcknowledged} />

        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
        <Button
          onClick={handleConfirm}
          color="primary"
          variant="contained"
          disabled={submitting || (needsAck && !acknowledged)}
        >
          {submitting ? "Working…" : primaryLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
